import { TFunction } from 'i18next';

import {
	DataPlaneMerchantSettings,
	MakeApplePayPaymentResponse,
	PaymentLabel,
	QuickPayRelayResultStatus,
	QuickPayRelayUserInvalidFields,
} from '../@types/types';

export enum APPLEPAY_ERROR_TYPE {
	UNKNOWN,
	FORM_VALIDATION,
	PAYMENT_CONFIRMATION,
	CONTACT_VALIDATION,
	PAYMENT_REQUEST,
}

export type ApplePayLocalError = {
	type: APPLEPAY_ERROR_TYPE;
	message: {
		title?: string;
		subtitle: string;
	};
};

export enum MerchantCardPreference {
	AnyCard = 0,
	DebitPreferred = 1,
	DebitOnly = 2,
}

export enum ApplePayMerchantCapabilities {
	Supports3DS = 'supports3DS',
	SupportsCredit = 'supportsCredit',
	SupportsDebit = 'supportsDebit',
}

export function getApplePayNetworksAndCapabilities(merchantConfig: DataPlaneMerchantSettings) {
	const { SupportedCardBrands, CardPreference } = merchantConfig;

	const supportsCards = SupportedCardBrands && SupportedCardBrands.length > 0;

	if (supportsCards) {
		let merchantCapabilities: ApplePayMerchantCapabilities[] = [];
		switch (CardPreference ?? MerchantCardPreference.AnyCard.toString()) {
			case MerchantCardPreference.DebitOnly.toString():
				merchantCapabilities = [
					ApplePayMerchantCapabilities.Supports3DS,
					ApplePayMerchantCapabilities.SupportsDebit,
				];
				break;
			case MerchantCardPreference.AnyCard.toString():
			case MerchantCardPreference.DebitPreferred.toString():
			default:
				merchantCapabilities = [
					ApplePayMerchantCapabilities.Supports3DS,
					ApplePayMerchantCapabilities.SupportsCredit,
					ApplePayMerchantCapabilities.SupportsDebit,
				];
				break;
		}

		return {
			supportedNetworks: SupportedCardBrands.map(brand => brand.toLowerCase()),
			merchantCapabilities,
		};
	}

	return false;
}

export function getApplePayPaymentRequest(
	config: DataPlaneMerchantSettings,
	paymentLabel: PaymentLabel,
	amount: string
): ApplePayJS.ApplePayPaymentRequest | null {
	const data = getApplePayNetworksAndCapabilities(config);
	if (data) {
		const { ApplePayMerchantName, CurrencyCode, CountryCode } = config;
		const label = paymentLabel.NounSentenceCase;
		const lineItems = [{ label, amount }];

		return {
			currencyCode: CurrencyCode,
			countryCode: CountryCode,
			lineItems,
			total: {
				label: `${ApplePayMerchantName}`,
				amount,
			},
			...data,
			shippingMethods: [],
			requiredBillingContactFields: ['postalAddress'],
			requiredShippingContactFields: ['email', 'name', 'phone'],
		};
	}

	return null;
}

export enum ApplePayPaymentMethodType {
	Debit = 0,
	Credit = 1,
	Prepaid = 2,
	Store = 3,
}

export enum ApplePayPaymentPassActivationState {
	Activated = 0,
	RequiresActivation = 1,
	Activating = 2,
	Suspended = 3,
	Deactivated = 4,
}

export const getApplePaymentMethodType = (type: ApplePayJS.ApplePayPaymentMethodType) => {
	switch (type) {
		case 'prepaid':
			return ApplePayPaymentMethodType.Prepaid;
		case 'credit':
			return ApplePayPaymentMethodType.Credit;
		case 'store':
			return ApplePayPaymentMethodType.Store;
		case 'debit':
		default:
			return ApplePayPaymentMethodType.Debit; // Default to Debit for unexpected inputs
	}
};

export const getAppleContact = (contact: ApplePayJS.ApplePayPaymentContact | undefined) => ({
	addressLine1: contact && contact.addressLines && contact.addressLines.length >= 1 ? contact.addressLines[0] : '',
	addressLine2: contact && contact.addressLines && contact.addressLines.length >= 2 ? contact.addressLines[1] : '',
	city: (contact ? contact.locality : '') || '',
	state: (contact ? contact.administrativeArea : '') || '',
	zip: (contact ? contact.postalCode : '') || '',
	country: (contact ? contact.countryCode : '') || '',
});

export const getApplePayer = (contact: ApplePayJS.ApplePayPaymentContact | undefined) => {
	if (!contact) {
		return {
			invalid: true,
			missing: ['emailAddress', 'firstName', 'lastName', 'mobileNumber'],
			payer: null,
		};
	}

	const details = {
		emailAddress: contact.emailAddress || '',
		firstName: (contact.givenName || '').trim(),
		lastName: (contact.familyName || '').trim(),
		mobileNumber: contact.phoneNumber || '',
	};

	const hasAllDetails = Object.values(details).every(value => !!value);

	return {
		invalid: !hasAllDetails,
		missing: Object.keys(details)
			.map(key => (!details[key as keyof typeof details] ? key : undefined))
			.filter(Boolean) as string[],
		payer: details,
	};
};

export const getAPInvalidFieldErrorsTranslation = (
	translation: TFunction<'common', undefined>
): Record<string, string> => ({
	[QuickPayRelayUserInvalidFields.MobileNumber.toString()]: translation('applePayErrors.fields.mobileNumber'),
	[QuickPayRelayUserInvalidFields.EmailAddress.toString()]: translation('applePayErrors.fields.emailAddress'),
	[QuickPayRelayUserInvalidFields.FirstName.toString()]: translation('applePayErrors.fields.firstName'),
	[QuickPayRelayUserInvalidFields.LastName.toString()]: translation('applePayErrors.fields.lastName'),
	[QuickPayRelayUserInvalidFields.Amount.toString()]: translation('applePayErrors.fields.amount'),
});

const isSuccessfulPaymentStatus = (result: MakeApplePayPaymentResponse) => {
	switch (result.status) {
		case QuickPayRelayResultStatus.Poll:
		case QuickPayRelayResultStatus.TimedOut:
		case QuickPayRelayResultStatus.Processing:
		case QuickPayRelayResultStatus.Delayed:
			return true;
		default:
			return false;
	}
};

export const isApplePayPaymentSuccessful = (result: MakeApplePayPaymentResponse | undefined | null): boolean => {
	if (!result || !result.success) return false;

	if (result.success || result.status === QuickPayRelayResultStatus.Success) {
		return true;
	}

	return isSuccessfulPaymentStatus(result);
};

export const shouldCheckEmail = (result: MakeApplePayPaymentResponse) => isSuccessfulPaymentStatus(result);
